import React, { useState } from 'react';
import { FileText, Download, Book, Shield, Search, Clock, TrendingUp } from 'lucide-react';

interface Resource {
  id: string;
  title: string;
  description: string;
  type: 'template' | 'checklist' | 'guide' | 'tool';
  category: 'proposal' | 'compliance' | 'cost' | 'contracting';
  format: 'pdf' | 'xlsx' | 'docx';
  icon: React.ElementType;
  downloadUrl: string;
  downloads: number;
}

const resources: Resource[] = [
  {
    id: '1',
    title: 'Government Proposal Template',
    description: 'Comprehensive template for federal contract proposals',
    type: 'template',
    category: 'proposal',
    format: 'docx',
    icon: FileText,
    downloadUrl: '#',
    downloads: 1250,
  },
  {
    id: '2',
    title: 'Compliance Checklist Bundle',
    description: 'Complete checklist for federal contracting compliance',
    type: 'checklist',
    category: 'compliance',
    format: 'pdf',
    icon: Shield,
    downloadUrl: '#',
    downloads: 890,
  },
  {
    id: '3',
    title: 'Cost Estimation Spreadsheet',
    description: 'Template for accurate contract cost estimation',
    type: 'tool',
    category: 'cost',
    format: 'xlsx',
    icon: FileText,
    downloadUrl: '#',
    downloads: 750,
  },
  {
    id: '4',
    title: 'Federal Contracting Guide 2024',
    description: 'Comprehensive guide to federal contracting',
    type: 'guide',
    category: 'contracting',
    format: 'pdf',
    icon: Book,
    downloadUrl: '#',
    downloads: 2100,
  },
];

const categories = [
  { id: 'proposal', label: 'Proposal Templates' },
  { id: 'compliance', label: 'Compliance Checklists' },
  { id: 'cost', label: 'Cost Estimation Tools' },
  { id: 'contracting', label: 'Contracting Guides' },
];

const formatLabels: Record<string, string> = {
  pdf: 'PDF',
  xlsx: 'Excel',
  docx: 'Word',
};

export function ResourceHub() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<string | null>(null);

  const filteredResources = resources
    .filter((resource) => {
      const matchesSearch = resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        resource.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = !selectedCategory || resource.category === selectedCategory;
      const matchesFormat = !selectedFormat || resource.format === selectedFormat;
      return matchesSearch && matchesCategory && matchesFormat;
    })
    .sort((a, b) => b.downloads - a.downloads);

  const popularResources = [...resources]
    .sort((a, b) => b.downloads - a.downloads)
    .slice(0, 3);

  return (
    <div className="space-y-8">
      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search resources..."
            className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            className="rounded-md border border-gray-300 py-1.5 pl-3 pr-8 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            value={selectedCategory || ''}
            onChange={(e) => setSelectedCategory(e.target.value || null)}
          >
            <option value="">All Categories</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.label}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border border-gray-300 py-1.5 pl-3 pr-8 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            value={selectedFormat || ''}
            onChange={(e) => setSelectedFormat(e.target.value || null)}
          >
            <option value="">All Formats</option>
            {Object.entries(formatLabels).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Most Downloaded */}
      <div>
        <h3 className="mb-4 text-lg font-medium text-gray-900">Most Downloaded Resources</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {popularResources.map((resource) => (
            <div
              key={resource.id}
              className="flex flex-col rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="rounded-lg bg-indigo-50 p-3">
                    <resource.icon className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{resource.title}</h4>
                    <p className="text-sm text-gray-500">{formatLabels[resource.format]}</p>
                  </div>
                </div>
                <div className="flex items-center text-sm text-gray-500">
                  <TrendingUp className="mr-1 h-4 w-4" />
                  {resource.downloads.toLocaleString()} downloads
                </div>
              </div>
              <p className="mt-4 flex-1 text-sm text-gray-500">{resource.description}</p>
              <div className="mt-6">
                <button className="inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700">
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* All Resources */}
      <div>
        <h3 className="mb-4 text-lg font-medium text-gray-900">All Resources</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filteredResources.map((resource) => (
            <div
              key={resource.id}
              className="flex flex-col rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
            >
              <div className="flex items-center space-x-3">
                <div className="rounded-lg bg-indigo-50 p-3">
                  <resource.icon className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{resource.title}</h4>
                  <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                    <span>{formatLabels[resource.format]}</span>
                    <span>•</span>
                    <span className="flex items-center">
                      <Clock className="mr-1 h-4 w-4" />
                      {resource.downloads.toLocaleString()} downloads
                    </span>
                  </div>
                </div>
              </div>
              <p className="mt-4 flex-1 text-sm text-gray-500">{resource.description}</p>
              <div className="mt-6">
                <button className="inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700">
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}